import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Col, ListGroup, ListGroupItem, Row } from 'reactstrap';
import { useAuthProvider } from 'utils/api/useAuthProvider';
import ButtonWithIcon from '../../components/ButtonWithIcon/ButtonWithIcon';
import { COMPANY_OBJECTIVES } from '../../consts/consts';
import {
  InitOrganization,
  InitProxyPerson,
  Me,
  Person,
  PotentiallyAdminablePerson,
  TimeFrame,
} from '../../types';
import {
  OBJECTIVE_TYPE_SCOPES,
  OBJECTIVE_VIEW_TYPES,
  Objective,
  ObjectiveViewType,
  ObjectiveWithRef,
} from '../../utils/models/Objective';
import { peopleIdsAreEqual } from '../../utils/models/Person';
import {
  EventSerializerProvider,
  useEventSerializer,
} from '../../utils/util/EventSerializer';
import { useDeepEqualMemo } from '../../utils/util/hooks';
import ObjectiveAutosavePrompt from '../Objectives/ObjectiveAutosavePrompt';
import {
  defaultObjectiveTypeIdForViewType,
  getAllTreeObjectives,
  isRetryableObjectiveSaveError,
  OBJECTIVE_SAVE_MAX_RETRY_ATTEMPTS,
  useObjectiveActions,
  useObjectiveOperations,
} from '../Objectives/ObjectiveOperations';
import ObjectivesEmpty from '../Objectives/ObjectivesEmpty';
import ObjectivesTimeFrameSelector, {
  useObjectivesTimeFrameSelector,
} from '../Objectives/ObjectivesTimeFrameSelector';
import { useObjectiveTypes } from '../Objectives/ObjectiveTypeOperations';
import ReactTagsInput from '../Widgets/Inputs/ReactTagsInput';
import Loading from '../Widgets/Loading';
import PersonalObjectiveItemRoot from './PersonalObjectiveItemRoot';

interface Props {
  currentOrganization: InitOrganization;
  currentProxyPerson: InitProxyPerson;
  me: Me;
  person: PotentiallyAdminablePerson;
  isReadOnly?: boolean;
  // TODO: externalize the following props
  isInPopover?: boolean;
  showTeamNav?: boolean;
  renderTeamNav?: (
    person: Person,
    currentFirstDayOfTimeFrame: Date,
    firstDay: string,
    lastDay: string
  ) => React.ReactNode;
  hideDatePicker?: boolean;
  hideScoresAndWeight?: boolean;
  hideRelatedObjectives?: boolean;
  showMiniEmptyStateCallToAction?: boolean;
  shouldCacheObjectivesOnFrontend?: boolean;
  isDemoOrPreviewMode?: boolean;
  demoObjectives?: Objective[];
  defaultTimeFrame?: TimeFrame;
  className?: string;
  getObjectives?: (
    objectives: Objective[],
    currentTimeframeText?: string
  ) => void; //this is a callback function that picks the objectives from this component..... rename it onChangeObjectives or something like that
  autoFocus?: boolean;
  // High-level view mode driving both the backend tree query (sent as the
  // `view_type` URL param — backend translates to type_scopes + admin
  // inheritance) and the local UI affordances (which objective types the
  // creation buttons offer). See `OBJECTIVE_VIEW_TYPES` for the values.
  viewType: ObjectiveViewType;
}

type ApiActionState = 'LOADING' | 'FETCHED' | 'RELOAD'; // TODO: add ERROR

const PersonalObjectives: FC<Props> = ({
  currentProxyPerson,
  currentOrganization,
  me,
  person,
  isReadOnly = false, // This is to force the veiw to force the view to be read only. TODO: remove this and externalize the widget
  isInPopover = false,
  showTeamNav = false,
  renderTeamNav,
  hideDatePicker = false,
  hideScoresAndWeight = false,
  hideRelatedObjectives = false,
  showMiniEmptyStateCallToAction = false,
  shouldCacheObjectivesOnFrontend = false,
  isDemoOrPreviewMode = false,
  demoObjectives = [],
  defaultTimeFrame,
  className,
  getObjectives = () => {
    // do nothing
  },
  autoFocus = false,
  viewType,
}) => {
  const { user } = useAuthProvider();
  const { organizationObjectiveTypes, userEditableObjectiveTypes } =
    useObjectiveTypes();
  const [state, setState] = React.useState<ApiActionState>('LOADING');
  const [objectives, setObjectives] = React.useState<ObjectiveWithRef[]>([]);
  const [editableObjectiveIds, setEditableObjectiveIds] = React.useState<
    ReadonlySet<number>
  >(() => new Set());
  const userMemo = useDeepEqualMemo(user);
  const [companyOKRsFullVisibility, setCompanyOKRsFullVisibility] =
    useState(true);

  const handleChangeTimeFrame = useCallback(() => {
    setState('LOADING');
    setObjectives([]);
  }, []);

  const {
    currentFirstDayOfTimeFrame,
    currentTimeframeText,
    viewPreviousTimeframe,
    viewNextTimeframe,
    firstDay,
    lastDay,
  } = useObjectivesTimeFrameSelector({
    defaultTimeFrame,
    onChangeTimeFrame: handleChangeTimeFrame,
  });

  // TODO: here instead of using useffect, hook the getObjective on the setObjectives, making a wrapper method instead
  useEffect(() => {
    getObjectives(objectives, currentTimeframeText);
  }, [objectives, getObjectives, currentTimeframeText]);

  const { onSubmit, onSuccess, onError } = useObjectiveOperations({
    isDemoOrPreviewMode,
    currentOrganization,
    currentProxyPerson,
    person,
    firstDay,
    lastDay,
    setObjectives,
    setState,
    viewType,
  });

  // TODO Move this logic to the backend or at least to a function
  const isMe = useMemo(() => {
    return peopleIdsAreEqual(person?.id, me.id);
  }, [me.id, person?.id]);

  const isEditable = useMemo(
    () =>
      !isReadOnly &&
      (isMe || person?.is_adminable || person?.is_below_in_chain_of_command),
    [isMe, isReadOnly, person]
  );

  const onFetchedObjectives = useCallback(
    (
      objectives,
      companyOKRsFullVisibility,
      editableObjectiveIdsList: number[] = []
    ) => {
      setState((state) => (state === 'FETCHED' ? 'RELOAD' : 'FETCHED'));
      setObjectives(objectives);
      setCompanyOKRsFullVisibility(companyOKRsFullVisibility);
      setEditableObjectiveIds(new Set(editableObjectiveIdsList));
    },
    [setState, setObjectives, setCompanyOKRsFullVisibility]
  );

  // The components are uncontrolled, so a full reload is needed for the objectives are externally updated
  useEffect(() => {
    if (state === 'RELOAD') {
      setState('FETCHED');
    }
  }, [state]);

  useEffect(() => {
    if (!isDemoOrPreviewMode) {
      getAllTreeObjectives({
        person,
        currentOrganization,
        currentProxyPerson,
        firstDay,
        lastDay,
        shouldCacheObjectivesOnFrontend,
        user: userMemo,
        onFetchedObjectives,
        viewType,
      });
    }
  }, [
    currentOrganization,
    currentProxyPerson,
    firstDay,
    isDemoOrPreviewMode,
    lastDay,
    onFetchedObjectives,
    person,
    shouldCacheObjectivesOnFrontend,
    viewType,
    userMemo,
  ]);

  useEffect(() => {
    if (isDemoOrPreviewMode && demoObjectives && state !== 'FETCHED') {
      onFetchedObjectives(demoObjectives, true, [person.id]);
    }
  }, [
    demoObjectives,
    isDemoOrPreviewMode,
    onFetchedObjectives,
    person.id,
    state,
  ]);

  return (
    <EventSerializerProvider
      onSubmit={onSubmit}
      onErrorCallback={onError}
      onSuccessCallback={onSuccess}
      maxRetryAttempts={OBJECTIVE_SAVE_MAX_RETRY_ATTEMPTS}
      isRetryable={isRetryableObjectiveSaveError}
    >
      <ObjectiveAutosavePrompt />
      <MainArea
        autoFocus={autoFocus}
        person={person}
        setObjectives={setObjectives}
        objectives={objectives}
        firstDay={firstDay}
        lastDay={lastDay}
        currentOrganization={currentOrganization}
        hideScoresAndWeight={hideScoresAndWeight}
        hideDatePicker={hideDatePicker}
        isInPopover={isInPopover}
        showMiniEmptyStateCallToAction={showMiniEmptyStateCallToAction}
        hideRelatedObjectives={hideRelatedObjectives}
        showTeamNav={showTeamNav}
        renderTeamNav={renderTeamNav}
        isEditable={isEditable}
        viewNextTimeframe={viewNextTimeframe}
        viewPreviousTimeframe={viewPreviousTimeframe}
        currentTimeframeText={currentTimeframeText}
        state={state}
        currentFirstDayOfTimeFrame={currentFirstDayOfTimeFrame}
        me={me}
        companyOKRsFullVisibility={companyOKRsFullVisibility}
        editableObjectiveIds={editableObjectiveIds}
        className={className}
        isDemoOrPreviewMode={isDemoOrPreviewMode}
        organizationObjectiveTypes={organizationObjectiveTypes}
        userEditableObjectiveTypes={userEditableObjectiveTypes}
        viewType={viewType}
      />
    </EventSerializerProvider>
  );
};

const MainArea = ({
  person,
  setObjectives,
  objectives,
  firstDay,
  lastDay,
  currentOrganization,
  hideScoresAndWeight,
  hideDatePicker,
  showMiniEmptyStateCallToAction,
  hideRelatedObjectives,
  isInPopover,
  showTeamNav,
  renderTeamNav,
  isEditable,
  className,
  viewNextTimeframe,
  viewPreviousTimeframe,
  currentTimeframeText,
  state,
  currentFirstDayOfTimeFrame,
  me,
  companyOKRsFullVisibility,
  editableObjectiveIds,
  isDemoOrPreviewMode,
  organizationObjectiveTypes,
  userEditableObjectiveTypes,
  viewType,
  autoFocus = false,
}) => {
  const defaultObjectiveTypeId = defaultObjectiveTypeIdForViewType(
    viewType,
    userEditableObjectiveTypes
  );

  const { createNewTopItem } = useObjectiveActions({
    me,
    person,
    firstDay,
    lastDay,
    currentOrganization,
    defaultObjectiveTypeId,
    userEditableObjectiveTypes,
  });

  const teamNav = useMemo(
    () =>
      renderTeamNav?.(person, currentFirstDayOfTimeFrame, firstDay, lastDay),
    [currentFirstDayOfTimeFrame, firstDay, lastDay, person, renderTeamNav]
  );

  return (
    <Row
      // in hovers, we need to extend the width explicitly on hover, else the
      // hovers are super thin and super super long
      style={
        isInPopover && state === 'FETCHED' && objectives.length > 0
          ? { width: '50vw' }
          : undefined
      }
    >
      <Col
        className={
          className ??
          (!hideScoresAndWeight && !isInPopover
            ? `mb-n4 col-12 ${
                showTeamNav ? 'col-md-9' : 'col-md-10 offset-md-1'
              }`
            : '')
        }
      >
        {!hideDatePicker && (
          <ObjectivesTimeFrameSelector
            viewNextTimeframe={viewNextTimeframe}
            currentTimeframeText={currentTimeframeText}
            viewPreviousTimeframe={viewPreviousTimeframe}
            disabled={state === 'LOADING' || isDemoOrPreviewMode}
          />
        )}
        {state === 'LOADING' && <Loading />}
        {state === 'FETCHED' && objectives.length === 0 && (
          <ObjectivesEmpty
            person={person}
            isEditable={isEditable}
            isInPopover={isInPopover}
            me={me}
            currentTimeframeText={currentTimeframeText}
            objectivesForTimeframe={objectives}
            addObjective={() => createNewTopItem({ setObjectives })}
            showMiniEmptyStateCallToAction={showMiniEmptyStateCallToAction}
            autoFocus={autoFocus}
          />
        )}
        {state === 'FETCHED' && objectives.length > 0 && (
          <PersonalObjectiveItemRootList
            autoFocus={autoFocus && isEditable}
            objectives={objectives}
            isEditable={isEditable}
            isInPopover={isInPopover}
            hideScoresAndWeight={hideScoresAndWeight}
            hideRelatedObjectives={hideRelatedObjectives}
            collapseObjectivesByDefault={showMiniEmptyStateCallToAction}
            person={person}
            setObjectives={setObjectives}
            firstDay={firstDay}
            lastDay={lastDay}
            createNewTopItem={() => createNewTopItem({ setObjectives })}
            companyOKRsFullVisibility={companyOKRsFullVisibility}
            editableObjectiveIds={editableObjectiveIds}
            isDemoOrPreviewMode={isDemoOrPreviewMode}
            organizationObjectiveTypes={organizationObjectiveTypes}
            userEditableObjectiveTypes={userEditableObjectiveTypes}
            viewType={viewType}
          />
        )}
      </Col>
      {showTeamNav && teamNav}
    </Row>
  );
};

const PersonalObjectiveItemRootList = ({
  objectives,
  person,
  setObjectives,
  firstDay,
  lastDay,
  createNewTopItem,
  isEditable = false,
  isInPopover = false,
  hideScoresAndWeight = false,
  hideRelatedObjectives = false,
  collapseObjectivesByDefault = false,
  companyOKRsFullVisibility,
  editableObjectiveIds,
  isDemoOrPreviewMode = false,
  organizationObjectiveTypes,
  userEditableObjectiveTypes,
  viewType,
  autoFocus = false,
}) => {
  const { formatMessage } = useIntl();
  const [addToBufferQueue] = useEventSerializer();
  const [searchOpen, setSearchOpen] = useState(false);

  const isIndividualContext = viewType !== OBJECTIVE_VIEW_TYPES.STRATEGIC;
  const canCreateStrategicObjective = userEditableObjectiveTypes.some(
    (t) => t.scope === OBJECTIVE_TYPE_SCOPES.STRATEGIC
  );

  const [importedObjective, setImportedObjective] = useState<Objective>();

  return (
    <ListGroup>
      {objectives.map((objective, index) => (
        <PersonalObjectiveItemRoot
          autoFocus={autoFocus}
          isEditable={isEditable}
          isInPopover={isInPopover}
          hideScoresAndWeight={hideScoresAndWeight}
          hideRelatedObjectives={hideRelatedObjectives}
          person={person}
          key={objective.force_key ?? objective.key}
          objective={objective}
          showLabelHeadings={index === 0}
          forceShowChildren={
            !collapseObjectivesByDefault &&
            (objective?.children || []).length > 0
          }
          // TODO: rename this for a more generic CALLBACK name. It's not just updating.
          updateParentObjective={(deletedObjective) => {
            setObjectives((objectives) =>
              objectives.filter((o) => o.key !== deletedObjective.key)
            );
          }}
          enableParentEdit={companyOKRsFullVisibility && !isDemoOrPreviewMode}
          editableObjectiveIds={editableObjectiveIds}
          organizationObjectiveTypes={organizationObjectiveTypes}
          userEditableObjectiveTypes={userEditableObjectiveTypes}
          inlineTypeBadge={viewType === OBJECTIVE_VIEW_TYPES.STRATEGIC}
        />
      ))}
      {/* create new Root object */}
      {isEditable && (
        <ListGroupItem color="white">
          <>
            {!searchOpen ? (
              <div className="d-flex gap-3">
                <ButtonWithIcon
                  color="link"
                  icon="plus"
                  onClick={createNewTopItem}
                >
                  <FormattedMessage
                    id="app.views.person.personal_objectives.add_new_objective"
                    defaultMessage="Add new objective"
                  />
                </ButtonWithIcon>
                {companyOKRsFullVisibility && isIndividualContext && (
                  <ButtonWithIcon
                    color="link"
                    icon="search"
                    onClick={() => setSearchOpen(true)}
                  >
                    <FormattedMessage
                      id="app.views.person.personal_objectives.search_existing"
                      defaultMessage="Add existing objectives"
                    />
                  </ButtonWithIcon>
                )}
                {companyOKRsFullVisibility &&
                  isIndividualContext &&
                  canCreateStrategicObjective && (
                    <a
                      className="btn btn-sm btn-link d-inline-flex align-items-center"
                      href={COMPANY_OBJECTIVES(formatMessage).path}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fe fe-plus me-2" />
                      <FormattedMessage
                        id="app.views.person.personal_objectives.create_strategic_objective_inline"
                        defaultMessage="Create strategic objective"
                      />
                      <i className="fe fe-external-link ms-2" />
                    </a>
                  )}
              </div>
            ) : !importedObjective ? (
              <div className="d-flex gap-3">
                <div className="flex-grow-1">
                  <ReactTagsInput
                    autoFocus={true}
                    placeholder={formatMessage({
                      id: 'app.views.person.personal_objectives.plceholder',
                      defaultMessage: 'Start typing to search for an objective',
                    })}
                    allowNew={false}
                    elasticsearchOptions={{
                      url: 'get-objectives-for-linking/v2',
                      index: 'objectives',
                      getQuery: (q) => ({
                        query: q,
                        owner_person_id: person.id,
                        coverage_start_date: firstDay,
                        coverage_end_date: lastDay,
                        is_importing: true,
                        // tags: tags.map((x) => x.id),
                        apply_feature_filters: true,
                      }),
                    }}
                    callback={(e) => {
                      if (!e || e.length === 0) {
                        return;
                      }
                      setImportedObjective(e[0].object);
                    }}
                    useTagCards={true}
                  />
                </div>
                <ButtonWithIcon
                  className="d-flex align-items-center text-nowrap"
                  outline
                  icon="x"
                  onClick={() => setSearchOpen(false)}
                >
                  <FormattedMessage
                    id="app.views.person.personal_objectives.dismiss"
                    defaultMessage="Dismiss"
                  />
                </ButtonWithIcon>
              </div>
            ) : (
              <div className="d-flex align-items-baseline gap-3">
                <div className="flex-grow-1 text-secondary">
                  <div>
                    <p>
                      <FormattedMessage
                        id="app.views.person.personal_objectives.add_self_as_collaborator"
                        defaultMessage="Do you wish to add yourself as a collaborator to this existing objective?"
                      />
                    </p>
                    <i className="fe fe-arrow-right" /> {importedObjective.name}
                  </div>
                </div>
                <ButtonWithIcon
                  className="d-flex align-items-center text-nowrap"
                  outline
                  icon="check"
                  color="primary"
                  onClick={() => {
                    if (!importedObjective) {
                      return;
                    }

                    const updted = {
                      ...importedObjective,
                      collaborators: [
                        ...importedObjective.collaborators,
                        person,
                      ],
                      related_to: [], // set temporarly to empty to avoid error. is loading anuyway
                    };
                    setObjectives([...objectives, updted]);

                    addToBufferQueue({
                      ...updted,
                      operation: 'IMPORT',
                    });
                    setSearchOpen(false);
                    setImportedObjective(undefined);
                  }}
                >
                  <FormattedMessage
                    id="app.views.person.personal_objectives.search_confirm"
                    defaultMessage="Confirm"
                  />
                </ButtonWithIcon>
                <ButtonWithIcon
                  className="d-flex align-items-center text-nowrap"
                  outline
                  icon="x"
                  onClick={() => {
                    setSearchOpen(false);
                    setImportedObjective(undefined);
                  }}
                >
                  <FormattedMessage
                    id="app.views.person.personal_objectives.search_cancel"
                    defaultMessage="Cancel"
                  />
                </ButtonWithIcon>
              </div>
            )}
          </>
        </ListGroupItem>
      )}
    </ListGroup>
  );
};

const mapStateToProps = (state) => {
  const { currentOrganization, currentProxyPerson, me } = state;

  return {
    currentOrganization,
    currentProxyPerson,
    me,
  };
};

export default connect(mapStateToProps)(PersonalObjectives);
